<template>
  <div 
    class="schema-table-wrapper"
    :class="responsiveClasses"
    role="region"
    :aria-label="ariaLabel || 'Data table'"
    :aria-describedby="hasDescription ? `${tableId}-description` : null"
  >
    <!-- Hidden description for screen readers -->
    <div
      v-if="hasDescription"
      :id="`${tableId}-description`"
      class="sr-only"
    >
      {{ ariaDescription }}
    </div>
    
    <b-table
      v-bind="computedTableProps"
      :items="processedData"
      :fields="computedFields"
      :busy="loading"
      :table-style="computedTableProps.tableStyle"
      class="tbl-sml"
      @row-clicked="onRowClick"
      @sort-changed="onSortChanged"
    >
      <!-- Single unified slot loop. Renders BOTH custom cell slots AND per-column header-help
           slots from ONE v-for to avoid the Vue 2.6.10 compiler bug where multiple
           v-for + v-slot:[dynamic] blocks on the same component silently drop all but the last.
           NOTE: bootstrap-vue rc.20 has NO generic `head()` slot — header overrides must use the
           per-field `HEAD_<key>` slot, which is why the header entries are merged in here rather
           than rendered from a separate generic header slot. -->
      <template
        v-for="column in allSlotEntries"
        v-slot:[column.slotKey]="{ item, field, label }"
      >
        <!-- Per-column header help: label + `?` icon with a hover popover. The popover uses the
             <b-popover> COMPONENT with container="body" so it renders at the document root and is
             NOT clipped by the `.table-responsive` overflow wrapper. -->
        <template v-if="column.isHeaderHelp">
          <span :key="column.slotKey + '-hlbl'" class="schema-th-label">{{ label }}</span>
          <span
            :key="column.slotKey + '-hicon'"
            :id="headerHelpId(column.key)"
            :aria-label="column.headerHelpTitle || 'More information'"
            class="ml-1 schema-th-help pointer"
            role="button"
            tabindex="0"
            @click.stop
          >
            <i class="fa fa-question-circle" aria-hidden="true"></i>
          </span>
          <b-popover
            :key="column.slotKey + '-hpop'"
            :target="headerHelpId(column.key)"
            :title="column.headerHelpTitle || null"
            triggers="hover focus"
            placement="top"
            container="body"
            boundary="window"
          >
            <span v-html="column.headerHelp"></span>
          </b-popover>
        </template>
        <!-- Custom cell rendering (component / formatter / icon / boolean) -->
        <template v-else>
        <!-- Component column (e.g. StatusBadge, ReferenceCell) -->
        <component
          v-if="column.component"
          :key="column.key + '-comp'"
          :is="column.component"
          :value="getNestedValue(item, column.key)"
          :row="item"
          :field="field"
          :column="column"
          v-bind="column.componentProps || {}"
          v-on="$listeners"
        />
        <!-- Formatter column -->
        <span
          v-else-if="column.formatter && !column.icon && column.dataType !== 'boolean' && column.dataType !== 'bool'"
          :key="column.key + '-fmt'"
          :class="column.cellClass"
          :title="getCellTitle(item, column)"
        >
          {{ applyFormatter(getNestedValue(item, column.key), column.formatter) }}
        </span>
        <!-- Icon column with optional text -->
        <div
          v-else-if="column.icon"
          :key="column.key + '-icon'"
          class="icon-cell"
          :class="column.cellClass"
        >
          <i
            v-if="getColumnIcon(item, column)"
            :class="getColumnIcon(item, column)"
            :style="getIconStyle(item, column)"
            :title="getIconTitle(item, column)"
            aria-hidden="true"
          ></i>
          <span
            v-if="column.showText !== false && getNestedValue(item, column.key)"
            :class="['icon-text', column.textAlign && `text-${column.textAlign}`]"
          >
            {{ formatIconText(getNestedValue(item, column.key), column) }}
          </span>
        </div>
        <!-- Boolean indicator column -->
        <div
          v-else
          :key="column.key + '-bool'"
          class="boolean-indicator"
          :class="column.cellClass"
        >
          <i
            :class="getBooleanIconClass(getNestedValue(item, column.key), column.booleanStyle)"
            :title="getBooleanTitle(getNestedValue(item, column.key))"
            aria-hidden="true"
          ></i>
          <span
            v-if="column.showText !== false"
            class="ml-1"
          >
            {{ getBooleanText(getNestedValue(item, column.key)) }}
          </span>
        </div>
        </template>
      </template>

      <!-- Action column slot -->
      <template
        v-if="hasActionColumn"
        v-slot:actions="{ item, index }"
      >
        <div class="btn-group btn-group-sm" role="group" :aria-label="`Actions for row ${index + 1}`">
          <button
            v-for="action in schema.actions"
            :key="action.key || action.name"
            :class="['btn', action.class || 'btn-hc']"
            :disabled="isActionDisabled(action, item)"
            :aria-label="getActionAriaLabel(action, item, index)"
            :title="action.tooltip || action.label"
            @click="onActionClick(action, item, index)"
          >
            <i 
              v-if="action.icon"
              :class="action.icon" 
              aria-hidden="true"
            ></i>
            <span :class="{ 'sr-only': action.hideLabel }">
              {{ action.label }}
            </span>
          </button>
        </div>
      </template>
      
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
          <span class="ml-2">Loading...</span>
        </div>
      </template>

      <template #empty>
        <div class="schema-table-empty text-center my-4">
          <!-- Schema validation error state -->
          <div v-if="schemaValidationError" class="error-state">
            <i class="fas fa-exclamation-triangle text-danger fa-2x mb-2"></i>
            <h5 class="text-danger">Schema Configuration Error</h5>
            <p class="text-muted">{{ schemaValidationError }}</p>
            <small class="text-muted">
              Please check your schema configuration and ensure all required fields are present.
            </small>
          </div>
          
          <!-- No data state -->
          <div v-else-if="!hasActualData" class="empty-state">
            <!-- Specific empty message supplied: clean icon + message only -->
            <template v-if="emptyText">
              <i class="fa fa-search fa-3x text-muted mb-3 d-inline-block" aria-hidden="true"></i>
              <p class="text-muted mb-0">{{ emptyText }}</p>
            </template>
            <!-- Generic empty state -->
            <template v-else>
              <img 
                src="@/assets/img/tick_selector.png" 
                class="img-fluid d-inline-block mb-2"
                alt="No data"
                style="max-width: 64px; opacity: 0.6;"
              />
              <h5 class="text-muted">{{ emptyStateTitle }}</h5>
              <p class="text-muted">
                {{ emptyStateMessage }}
              </p>

              <!-- Show column headers when empty but schema is valid -->
              <div v-if="schema.columns && showHeadersOnEmpty" class="empty-headers mt-3">
                <small class="text-muted d-block mb-2">Available columns:</small>
                <div class="d-flex flex-wrap justify-content-center">
                  <span
                    v-for="column in visibleColumns"
                    :key="column.key"
                    class="badge badge-secondary mr-1 mb-1"
                  >
                    {{ column.label }}
                  </span>
                </div>
              </div>
            </template>
          </div>
          
          <!-- Loading error state -->
          <div v-else-if="dataLoadingError" class="error-state">
            <i class="fas fa-wifi text-warning fa-2x mb-2"></i>
            <h5 class="text-warning">Data Loading Error</h5>
            <p class="text-muted">{{ dataLoadingError }}</p>
            <button 
              v-if="onRetry" 
              class="btn btn-outline-primary btn-sm"
              @click="handleRetry"
            >
              <i class="fas fa-redo mr-1"></i>
              Retry
            </button>
          </div>
        </div>
      </template>
    </b-table>
    
    <!-- Pagination -->
    <b-pagination
      v-if="showPagination && totalRows > perPage"
      v-model="currentPage"
      :total-rows="totalRows"
      :per-page="perPage"
      :aria-controls="tableId"
      class="mt-3"
      align="center"
    />
  </div>
</template>

<script>
import _filters from '@/mixins/filters.js'
import { parseDataType, validateDataType } from '@/utils/data-type-parser'
import { validateSchema } from '@/utils/schema-validator'
import { handleFormatterError } from '@/utils/error-handler'
import formatters from './schema-table/formatters'
import { monitorPerformance, debounce } from '@/utils/error-handler'
import { ColumnWidthMixin } from '@/mixins/columnWidth'
import { calculateColumnWidths, enforceMinimumWidths } from '@/utils/tableWidth'
import { validateTableWidthConfiguration } from '@/utils/widthValidator'
import StatusBadge from './table/StatusBadge.vue'
import StatusCell from './table/StatusCell.vue'
import ReadingTypeIcon from './table/ReadingTypeIcon.vue'
import ReadingTypeFlagPill from './table/ReadingTypeFlagPill.vue'
import BillingStatusToggle from './table/BillingStatusToggle.vue'
import ReferenceCell from './table/ReferenceCell.vue'

export default {
  name: 'SchemaTable',

  components: {
    StatusBadge,
    StatusCell,
    ReadingTypeIcon,
    ReadingTypeFlagPill,
    BillingStatusToggle,
    ReferenceCell
  },

  mixins: [_filters, ColumnWidthMixin],
  
  props: {
    schema: {
      type: Object,
      required: true,
      validator: (schema) => {
        const isValid = schema.columns && Array.isArray(schema.columns) &&
               schema.columns.every(col => col.key && col.label)
        
        // Enhanced validation using schema validator
        if (isValid) {
          validateSchema(schema, 'SchemaTable')
        }
        
        return isValid
      }
    },
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    emptyText: {
      type: String,
      default: null
    },
    tableId: {
      type: String,
      default: () => `table-${Math.random().toString(36).substr(2, 9)}`
    },
    showHeadersOnEmpty: {
      type: Boolean,
      default: true
    },
    dataLoadingError: {
      type: String,
      default: null
    },
    onRetry: {
      type: Function,
      default: null
    },
    ariaLabel: {
      type: String,
      default: null
    },
    ariaDescription: {
      type: String,
      default: null
    },
    enableKeyboardNavigation: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      currentPage: 1
    }
  },
  
  computed: {
    computedFields() {
      // Validate width configuration first
      const widthValidation = validateTableWidthConfiguration(this.schema.columns)
      
      // Log validation warnings in development
      if (process.env.NODE_ENV === 'development' && widthValidation.warnings.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('SchemaTable width configuration warnings:', widthValidation.warnings)
      }
      
      // Detect fill columns for special handling
      const fillColumns = this.schema.columns
        .map((col, index) => ({ col, index }))
        .filter(({ col }) => col.width === '100%')
      
      // Calculate column widths if any columns have width specifications
      let calculatedWidths = null
      const hasWidthSpecs = this.schema.columns.some(col => col.width)
      const hasFixedPixelColumns = this.schema.columns.some(col => col.width && col.width.endsWith('px'))
      
      // Calculate column widths if any columns have width specifications  
      if (hasWidthSpecs && this.containerWidth) {
        // Account for actions column width when calculating available space
        const actionsColumnWidth = this.hasActionColumn ? 120 : 0
        // Account for table container padding (10px left + 10px right = 20px)
        const tablePadding = 20
        // Add buffer for borders, scrollbars, and rendering inconsistencies
        const bufferWidth = 15
        
        // Calculate space consumed by fixed pixel columns (exclude from calculations)
        const fixedPixelTotal = this.schema.columns
          .filter(col => col.width && col.width.endsWith('px'))
          .reduce((sum, col) => sum + parseInt(col.width.replace('px', '')), 0)
        
        const availableWidth = this.containerWidth - actionsColumnWidth - tablePadding - bufferWidth - fixedPixelTotal
        
        // Only calculate widths for non-fixed-pixel columns
        const columnsForCalculation = this.schema.columns.filter(col => !col.width || !col.width.endsWith('px'))
        
        // Ensure we have reasonable minimum space for content columns  
        if (availableWidth > 200 && columnsForCalculation.length > 0) { // Minimum reasonable width
          const widthCalculation = calculateColumnWidths(columnsForCalculation, availableWidth)
          const enforcedWidths = enforceMinimumWidths(widthCalculation.widths, 50)
          
          // Rebuild calculatedWidths array with proper indexing
          calculatedWidths = new Array(this.schema.columns.length).fill(null)
          let calculationIndex = 0
          
          this.schema.columns.forEach((col, index) => {
            if (!col.width || !col.width.endsWith('px')) {
              if (calculationIndex < enforcedWidths.length) {
                calculatedWidths[index] = enforcedWidths[calculationIndex]
                calculationIndex++
              }
            }
            // Fixed pixel columns get null, triggering direct schema usage
          })
          
          // Log performance warnings if needed
          if (widthCalculation.warnings.length > 0 && process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('SchemaTable width calculation warnings:', widthCalculation.warnings)
          }
        } else {
          // Container too small for flexible layout - keep fixed pixels, auto-size others
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn(`Container too small (${availableWidth}px available after actions, padding, and buffer) for width specifications - falling back to auto sizing`)
          }
        }
      }
      
      const fields = this.schema.columns
        .filter(col => col.visible !== false)
        .map((col, index) => {
          const field = {
            key: col.key,
            label: col.label,
            sortable: col.sortable !== false,
            class: col.class,
            thClass: [col.headerClass, col.align && `text-${col.align}`].filter(Boolean).join(' '),
            tdClass: [col.cellClass, col.align && `text-${col.align}`].filter(Boolean).join(' '),
            formatter: null
          }

          // Header help: optional `?` icon + hover popover rendered by the generic head() slot.
          // `headerHelp` is HTML popover content; `headerHelpTitle` is an optional popover title.
          if (col.headerHelp) {
            field.headerHelp = col.headerHelp
            field.headerHelpTitle = col.headerHelpTitle || null
          }
          
          const isFillColumn = col.width === '100%'
          const isFixedPixelWidth = col.width && col.width.endsWith('px')
          
          // Width handling: Use exact schema value for fixed pixels, calculated for others
          if (isFixedPixelWidth) {
            // For fixed pixel widths (e.g., "150px"), use EXACT schema value - NO CALCULATIONS
            field.thStyle = { 
              width: col.width,
              minWidth: col.width,
              maxWidth: col.width
            }
            field.tdStyle = { 
              width: col.width,
              minWidth: col.width,
              maxWidth: col.width,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }
          } else if (calculatedWidths && calculatedWidths[index] && !isFixedPixelWidth) {
            // Use calculated width for percentages, fill columns, and auto-sizing
            field.thStyle = { 
              width: calculatedWidths[index],
              minWidth: calculatedWidths[index],
              maxWidth: calculatedWidths[index]
            }
            field.tdStyle = { 
              width: calculatedWidths[index],
              minWidth: calculatedWidths[index],
              maxWidth: calculatedWidths[index],
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              // Fill columns should allow text wrapping for better content display
              whiteSpace: isFillColumn ? 'normal' : 'nowrap'
            }
            
            // Fill columns should also allow for content expansion
            if (isFillColumn) {
              field.tdStyle.minHeight = '2.25rem' // Ensure adequate row height
              field.tdStyle.verticalAlign = 'top'  // Align content to top for multi-line
            }
          } else if (col.width) {
            // Fallback to basic width handling for backwards compatibility
            field.thStyle = { width: col.width }
            field.tdStyle = { 
              width: col.width,
              whiteSpace: isFillColumn ? 'normal' : 'nowrap'
            }
          } else {
            // No width specified - let browser auto-size
            field.thStyle = null
            field.tdStyle = null
          }
          
          return field
        })
      
      // Automatically add actions column if actions are defined
      if (this.hasActionColumn) {
        fields.push({
          key: 'actions',
          label: '',
          sortable: false,
          class: 'actions-column',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '120px', minWidth: '120px', maxWidth: '120px' },
          tdStyle: { width: '120px', minWidth: '120px', maxWidth: '120px' }
        })
      }
      
      return fields
    },
    
    computedTableProps() {
      const { options = {} } = this.schema
      const hasWidthSpecs = this.schema.columns.some(col => col.width)
      const hasFixedPixelColumns = this.schema.columns.some(col => col.width && col.width.endsWith('px'))
      
      const props = {
        striped: options.striped !== false,
        hover: options.hover !== false,
        small: options.small !== false,
        responsive: options.responsive !== false,
        bordered: options.bordered === true,
        outlined: options.outlined === true,
        // Enable bootstrap-vue's empty slot so the #empty state renders instead of a blank body.
        showEmpty: options.showEmpty !== false,
        ...options
      }
      
      // CRITICAL: Use table-layout: fixed when there are fixed pixel columns
      // table-layout: fixed forces browser to respect exact pixel widths
      if (hasWidthSpecs || hasFixedPixelColumns) {
        props.tableStyle = {
          tableLayout: 'fixed',
          width: '100%',
          ...(props.tableStyle || {})
        }
      }
      
      return props
    },
    
    // Single unified list of all columns that need a custom scoped slot.
    // Avoids the Vue 2.6.10 compiler bug where only the LAST v-for+v-slot:[dynamic]
    // block on a component is compiled into scopedSlots.
    allSlotColumns() {
      return this.schema.columns.filter(col =>
        col.component ||
        (col.formatter && !col.icon && col.dataType !== 'boolean' && col.dataType !== 'bool') ||
        col.icon ||
        col.dataType === 'boolean' ||
        col.dataType === 'bool'
      )
    },

    // Columns that declare `headerHelp` get a per-field `HEAD_<key>` header slot (rc.20 has no
    // generic head slot). See `allSlotEntries`.
    headerHelpColumns() {
      return this.schema.columns.filter(col => col.headerHelp)
    },

    // Combined slot descriptors driving the SINGLE unified v-for in the template. Cell entries use
    // the raw field key as their slot name; header-help entries use the `HEAD_<key>` slot name that
    // bootstrap-vue rc.20 resolves for custom column headers. Merging both into one list keeps the
    // template to a single v-for + dynamic v-slot block (Vue 2.6.10 dynamic-slot compiler bug).
    allSlotEntries() {
      const cellEntries = this.allSlotColumns.map(col => ({ ...col, slotKey: col.key, isHeaderHelp: false }))
      const headerEntries = this.headerHelpColumns.map(col => ({ ...col, slotKey: `HEAD_${col.key}`, isHeaderHelp: true }))
      return [...cellEntries, ...headerEntries]
    },

    customColumns() {
      return this.schema.columns.filter(col => col.component)
    },
    
    formatterColumns() {
      return this.schema.columns.filter(col => 
        col.formatter && 
        !col.component && 
        col.dataType !== 'boolean' // Boolean columns handled separately
      )
    },
    
    booleanColumns() {
      return this.schema.columns.filter(col => 
        col.dataType === 'boolean' || col.dataType === 'bool'
      )
    },
    
    iconColumns() {
      return this.schema.columns.filter(col => 
        col.icon && 
        col.dataType !== 'boolean' // Boolean columns have their own icon handling
      )
    },
    
    hasActionColumn() {
      return this.schema.actions && this.schema.actions.length > 0
    },
    
    processedData() {
      return monitorPerformance('processData', () => {
        const startTime = performance.now()
        
        const processedRows = this.data.map(row => {
          const processedRow = { ...row }
          
          // Apply data type parsing for each column
          this.schema.columns.forEach(col => {
            if (col.dataType && processedRow.hasOwnProperty(col.key)) {
              const rawValue = this.getNestedValue(row, col.key)
              processedRow[col.key] = parseDataType(rawValue, col.dataType, col.key)
            }
          })
          
          // Add row variant
          processedRow._rowVariant = this.getRowVariant(row)
          
          // Freeze object for performance optimization (prevents Vue reactivity overhead)
          return Object.freeze(processedRow)
        })
        
        const processingTime = performance.now() - startTime
        
        // Warn if processing takes longer than 50ms for large datasets
        if (processingTime > 50 && this.data.length > 100) {
          console.warn(`⚠️ SchemaTable: Data processing took ${Math.round(processingTime)}ms for ${this.data.length} rows. Consider pagination or virtual scrolling for better performance.`)
        }
        
        return processedRows
      }, 50) // 50ms warning threshold
    },

    showPagination() {
      return this.schema.options && this.schema.options.pagination !== false
    },

    perPage() {
      return (this.schema.options && this.schema.options.perPage) || 10
    },

    totalRows() {
      return this.data.length
    },

    /**
     * Schema validation error message
     */
    schemaValidationError() {
      if (!this.schema) return 'Schema configuration is required'
      if (!this.schema.columns || !Array.isArray(this.schema.columns)) {
        return 'Schema must contain a valid columns array'
      }
      if (this.schema.columns.length === 0) {
        return 'Schema must contain at least one column definition'
      }
      
      // Check for required column properties
      const invalidColumns = this.schema.columns.filter(col => !col.key || !col.label)
      if (invalidColumns.length > 0) {
        return `Invalid column configuration: columns must have 'key' and 'label' properties`
      }
      
      return null // No validation errors
    },

    /**
     * Check if there is actual data to display
     */
    hasActualData() {
      return Array.isArray(this.data) && this.data.length > 0 && !this.schemaValidationError
    },

    /**
     * Get visible columns for empty state display
     */
    visibleColumns() {
      if (!this.schema || !this.schema.columns) return []
      return this.schema.columns.filter(col => col.visible !== false)
    },

    /**
     * Empty state title based on context
     */
    emptyStateTitle() {
      if (this.loading) return 'Loading...'
      if (this.dataLoadingError) return 'Loading Failed'
      return 'No Data Available'
    },

    /**
     * Empty state message based on context
     */
    emptyStateMessage() {
      if (this.loading) return 'Please wait while data is being loaded.'
      if (this.dataLoadingError) return 'Unable to load data. Please try again.'
      
      const columnCount = this.visibleColumns.length
      if (columnCount > 0) {
        return `This table is ready to display ${columnCount} column${columnCount === 1 ? '' : 's'} of data.`
      }
      
      return 'Configure your schema and provide data to see results here.'
    },

    /**
     * Whether the component has a description for accessibility
     */
    hasDescription() {
      return !!this.ariaDescription
    },

    /**
     * Accessibility attributes for the table
     */
    accessibilityProps() {
      const props = {
        role: 'table'
      }

      if (this.ariaLabel) {
        props['aria-label'] = this.ariaLabel
      }

      if (this.ariaDescription) {
        props['aria-describedby'] = `${this.tableId}-description`
      }

      if (this.loading) {
        props['aria-busy'] = 'true'
        props['aria-live'] = 'polite'
      }

      return props
    },

    /**
     * Responsive table configuration based on schema settings
     */
    responsiveConfig() {
      const options = this.schema.options || {}
      return {
        responsive: options.responsive !== false,
        stackedBreakpoint: options.stackedBreakpoint || 'md',
        hideColumnsOnMobile: options.hideColumnsOnMobile || [],
        priorityColumns: options.priorityColumns || []
      }
    },

    /**
     * CSS classes for responsive behavior
     */
    responsiveClasses() {
      const config = this.responsiveConfig
      const classes = []

      if (config.responsive) {
        classes.push('table-responsive')
      }

      if (config.stackedBreakpoint) {
        classes.push(`table-responsive-${config.stackedBreakpoint}`)
      }

      return classes.join(' ')
    }
  },
  
  methods: {
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => current && current[key], obj)
    },

    // Stable, HTML-id-safe target id for a column header's help popover.
    headerHelpId(key) {
      return 'schema-th-help-' + String(key).replace(/[^a-zA-Z0-9_-]/g, '-')
    },

    applyFormatter(value, formatter) {
      if (!formatter) return value
      
      try {
        // Handle string formatter references (existing filters)
        if (typeof formatter === 'string') {
          // Check built-in formatters first
          if (formatters[formatter]) {
            return formatters[formatter](value)
          }
          
          // Check Vue filters
          if (this.$options.filters[formatter]) {
            return this.$options.filters[formatter](value)
          }
          
          // Unknown formatter
          handleFormatterError(`Unknown formatter "${formatter}"`, 'applyFormatter', value)
          return value
        }
        
        // Handle function formatters
        if (typeof formatter === 'function') {
          return formatter(value)
        }
        
        // Handle formatter objects with type and options
        if (typeof formatter === 'object' && formatter.type) {
          const formatterFunc = formatters[formatter.type]
          if (formatterFunc) {
            return formatterFunc(value, formatter.options || {})
          }
          
          handleFormatterError(`Unknown formatter type "${formatter.type}"`, 'applyFormatter', value)
          return value
        }
        
        // Invalid formatter type
        handleFormatterError(`Invalid formatter type: ${typeof formatter}`, 'applyFormatter', value)
        return value
        
      } catch (error) {
        handleFormatterError(`Formatter execution failed: ${error.message}`, 'applyFormatter', value, error)
        return value // Return original value on error
      }
    },
    
    // Optional per-cell tooltip for text/formatter cells. `titleField` reads a value off the
    // row (e.g. the A4 legacy-value `_legacyFlagLabel`); `title` may be a string or fn.
    getCellTitle(item, column) {
      if (column.titleField) {
        const value = this.getNestedValue(item, column.titleField)
        return value === null || value === undefined ? null : String(value)
      }
      if (column.title) {
        return typeof column.title === 'function'
          ? column.title(this.getNestedValue(item, column.key), item)
          : String(column.title)
      }
      return null
    },

    getRowVariant(row) {
      const { rowVariant } = this.schema.options || {}
      if (typeof rowVariant === 'function') {
        return rowVariant(row)
      }
      if (typeof rowVariant === 'string') {
        return this.getNestedValue(row, rowVariant)
      }
      return null
    },
    
    onRowClick(item, index, event) {
      this.$emit('row-click', { item, index, event })
    },
    
    onSortChanged(context) {
      this.$emit('sort-changed', context)
    },

    onActionClick(action, item, index) {
      this.$emit('action-click', { action: action.key || action.name, item, index })
    },

    isActionDisabled(action, item) {
      if (typeof action.disabled === 'function') {
        return action.disabled(item)
      }
      if (typeof action.disabled === 'string') {
        return this.getNestedValue(item, action.disabled)
      }
      return action.disabled === true
    },

    /**
     * Get appropriate icon class for boolean values
     * @param {boolean} value - The boolean value
     * @param {string} style - Style preference ('check', 'circle', 'text', 'switch')
     * @returns {string} CSS classes for the icon
     */
    getBooleanIconClass(value, style = 'check') {
      const isTrue = Boolean(value)
      
      switch (style) {
        case 'check':
          return isTrue 
            ? 'fas fa-check-circle text-success' 
            : 'fas fa-times-circle text-danger'
        
        case 'circle':
          return isTrue
            ? 'fas fa-circle text-success'
            : 'fas fa-circle text-muted'
        
        case 'square':
          return isTrue
            ? 'fas fa-check-square text-success'
            : 'far fa-square text-muted'
        
        case 'thumb':
          return isTrue
            ? 'fas fa-thumbs-up text-success'
            : 'fas fa-thumbs-down text-danger'
        
        case 'toggle':
          return isTrue
            ? 'fas fa-toggle-on text-success'
            : 'fas fa-toggle-off text-muted'
        
        default:
          return isTrue
            ? 'fas fa-check text-success'
            : 'fas fa-times text-danger'
      }
    },

    /**
     * Get text representation for boolean values
     * @param {boolean} value - The boolean value  
     * @returns {string} Text representation
     */
    getBooleanText(value) {
      return value ? 'Yes' : 'No'
    },

    /**
     * Get tooltip title for boolean indicators
     * @param {boolean} value - The boolean value
     * @returns {string} Tooltip text
     */
    getBooleanTitle(value) {
      return value ? 'True/Active/Enabled' : 'False/Inactive/Disabled'
    },

    /**
     * Get icon class for a column based on data value
     * @param {Object} item - Row data item
     * @param {Object} column - Column configuration
     * @returns {string} FontAwesome icon classes
     */
    getColumnIcon(item, column) {
      const value = this.getNestedValue(item, column.key)
      
      if (!column.icon) return ''
      
      // Static icon (string)
      if (typeof column.icon === 'string') {
        return column.icon
      }
      
      // Dynamic icon mapping (object)
      if (typeof column.icon === 'object' && !Array.isArray(column.icon)) {
        // Value-based mapping
        if (column.icon.mapping) {
          return column.icon.mapping[value] || column.icon.default || 'fas fa-question-circle'
        }
        
        // Function-based icon selection
        if (typeof column.icon.selector === 'function') {
          try {
            return column.icon.selector(value, item) || column.icon.default || 'fas fa-question-circle'
          } catch (error) {
            console.warn(`Icon selector function failed for column ${column.key}:`, error)
            return column.icon.default || 'fas fa-question-circle'
          }
        }
        
        // Status-based icons
        if (column.icon.status) {
          const statusIcons = {
            active: 'fas fa-check-circle text-success',
            inactive: 'fas fa-times-circle text-danger',
            pending: 'fas fa-clock text-warning',
            success: 'fas fa-check text-success',
            error: 'fas fa-exclamation-triangle text-danger',
            warning: 'fas fa-exclamation-circle text-warning',
            info: 'fas fa-info-circle text-info'
          }
          return statusIcons[value] || statusIcons[String(value).toLowerCase()] || column.icon.default || 'fas fa-question-circle'
        }
      }
      
      return 'fas fa-question-circle'
    },

    /**
     * Get icon style based on column configuration and data
     * @param {Object} item - Row data item
     * @param {Object} column - Column configuration
     * @returns {Object} Style object
     */
    getIconStyle(item, column) {
      const value = this.getNestedValue(item, column.key)
      const style = {}
      
      // Color based on value
      if (column.colorMapping && typeof column.colorMapping === 'object') {
        const color = column.colorMapping[value] || column.colorMapping[String(value).toLowerCase()]
        if (color) {
          style.color = color
        }
      }
      
      // Size configuration
      if (column.iconSize) {
        style.fontSize = column.iconSize
      }
      
      return style
    },

    /**
     * Get tooltip title for icon
     * @param {Object} item - Row data item
     * @param {Object} column - Column configuration
     * @returns {string} Tooltip text
     */
    getIconTitle(item, column) {
      const value = this.getNestedValue(item, column.key)
      
      if (column.titleMapping && typeof column.titleMapping === 'object') {
        return column.titleMapping[value] || column.titleMapping[String(value).toLowerCase()] || String(value)
      }
      
      if (column.title) {
        return typeof column.title === 'function' ? column.title(value, item) : String(column.title)
      }
      
      return String(value || '')
    },

    /**
     * Format text that appears alongside icons
     * @param {any} value - The cell value
     * @param {Object} column - Column configuration
     * @returns {string} Formatted text
     */
    formatIconText(value, column) {
      if (column.textFormatter) {
        return this.applyFormatter(value, column.textFormatter)
      }
      
      // Apply text transformation
      if (column.textTransform) {
        const text = String(value || '')
        switch (column.textTransform) {
          case 'uppercase':
            return text.toUpperCase()
          case 'lowercase':
            return text.toLowerCase()
          case 'capitalize':
            return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
          case 'title':
            return text.replace(/\w\S*/g, (txt) => 
              txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
            )
          default:
            return text
        }
      }
      
      return String(value || '')
    },

    /**
     * Handle retry button click for data loading errors
     */
    handleRetry() {
      if (this.onRetry && typeof this.onRetry === 'function') {
        this.$emit('retry-requested')
        this.onRetry()
      }
    },

    /**
     * Get accessibility label for action buttons
     * @param {Object} action - Action configuration
     * @param {Object} item - Row data item
     * @param {number} index - Row index
     * @returns {string} Accessible label for the action
     */
    getActionAriaLabel(action, item, index) {
      // Check for custom aria label function
      if (action.ariaLabel && typeof action.ariaLabel === 'function') {
        return action.ariaLabel(item, index)
      }

      // Check for custom aria label string template
      if (action.ariaLabel && typeof action.ariaLabel === 'string') {
        return action.ariaLabel.replace(/\{(\w+)\}/g, (match, key) => {
          return this.getNestedValue(item, key) || match
        })
      }

      // Default aria label with row context
      const rowIdentifier = item.id || item.name || `item ${index + 1}`
      return `${action.label} for ${rowIdentifier}`
    }
  }
}
</script>

<style scoped>
.schema-table-wrapper .table-responsive {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.schema-table-wrapper .tbl-sml {
  margin-bottom: 0;
}

/* Sortable column indicator (deep — targets b-table's header cells) */
.schema-table-wrapper >>> th[aria-sort] {
  cursor: pointer;
}

.schema-table-wrapper >>> th[aria-sort]::after {
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  content: '\f0dc';
  margin-left: 0.4rem;
  font-size: 0.8em;
  opacity: 0.35;
}

.schema-table-wrapper >>> th[aria-sort="ascending"]::after {
  content: '\f0d8';
  opacity: 1;
  color: var(--primary);
}

.schema-table-wrapper >>> th[aria-sort="descending"]::after {
  content: '\f0d7';
  opacity: 1;
  color: var(--primary);
}

/* Action buttons should not show the btn-link hover underline */
.schema-table-wrapper >>> .actions-column .btn-link:hover,
.schema-table-wrapper >>> .actions-column .btn-link:focus {
  text-decoration: none;
}

.schema-table-wrapper .table > tbody > tr > td {
  vertical-align: middle;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}

/* Boolean indicator styling */
.boolean-indicator {
  display: flex;
  align-items: center;
}

.boolean-indicator i {
  font-size: 1rem;
}

.boolean-indicator .fa-check-circle,
.boolean-indicator .fa-thumbs-up,
.boolean-indicator .fa-toggle-on {
  color: #28a745 !important;
}

.boolean-indicator .fa-times-circle,
.boolean-indicator .fa-thumbs-down {
  color: #dc3545 !important;
}

.boolean-indicator .fa-circle.text-muted,
.boolean-indicator .fa-square.text-muted,
.boolean-indicator .fa-toggle-off {
  color: #6c757d !important;
}

/* Icon cell styling */
.icon-cell {
  display: flex;
  align-items: center;
}

.icon-cell i {
  font-size: 1rem;
  margin-right: 0.5rem;
}

.icon-text {
  flex: 1;
}

/* Priority/Status color indicators */
.icon-cell .text-success {
  color: #28a745 !important;
}

.icon-cell .text-danger {
  color: #dc3545 !important;
}

.icon-cell .text-warning {
  color: #ffc107 !important;
}

.icon-cell .text-info {
  color: #17a2b8 !important;
}

.icon-cell .text-primary {
  color: #007bff !important;
}

.icon-cell .text-secondary {
  color: #6c757d !important;
}

/* Responsive design enhancements */
@media (max-width: 767.98px) {
  .schema-table-wrapper .btn-group-sm {
    flex-direction: column;
  }
  
  .schema-table-wrapper .btn-group-sm .btn {
    width: 100%;
    margin-bottom: 0.25rem;
  }
  
  .schema-table-wrapper .btn-group-sm .btn:last-child {
    margin-bottom: 0;
  }

  /* Stack icon and text on mobile */
  .icon-cell {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .icon-cell i {
    margin-right: 0;
    margin-bottom: 0.25rem;
  }
}

@media (max-width: 575.98px) {
  .schema-table-wrapper .table-responsive {
    font-size: 0.875rem;
  }
  
  .schema-table-wrapper .btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }

  /* Hide less important columns on mobile */
  .schema-table-wrapper .table .d-none.d-md-table-cell {
    display: none !important;
  }
}

/* Accessibility enhancements */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Header help icon (`?`) rendered by the generic head() slot */
.schema-th-help {
  cursor: pointer;
  color: #28a745;
}

.schema-th-help .fa-question-circle {
  font-size: 0.9em;
}

.schema-th-help:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

/* Focus indicators for better keyboard navigation */
.schema-table-wrapper .btn:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

.schema-table-wrapper .table > thead > tr > th:focus,
.schema-table-wrapper .table > tbody > tr > td:focus {
  outline: 2px solid #007bff;
  outline-offset: -2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .schema-table-wrapper .table {
    border: 2px solid;
  }
  
  .schema-table-wrapper .table > thead > tr > th {
    border-bottom: 2px solid;
  }
}

/* Reduced motion preference */
@media (prefers-reduced-motion: reduce) {
  .schema-table-wrapper * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Print styles */
@media print {
  .schema-table-wrapper .btn-group {
    display: none;
  }
  
  .schema-table-wrapper .table {
    border-collapse: collapse;
  }
  
  .schema-table-wrapper .table > thead > tr > th,
  .schema-table-wrapper .table > tbody > tr > td {
    border: 1px solid black;
    padding: 0.25rem;
  }
}
</style>